import {
  ResistanceBandLevel,
  WeightSetData,
  ResistanceBandSetData,
  TimedSetData,
} from "../types/program";

// Get resistance band level label
export const getResistanceBandLevelLabel = (level: number): string => {
  switch (level) {
    case ResistanceBandLevel.EXTRA_LIGHT:
      return "Extra Light (5 kg / 10 lbs)";
    case ResistanceBandLevel.LIGHT:
      return "Light (11 kg / 25 lbs)";
    case ResistanceBandLevel.MEDIUM:
      return "Medium (20 kg / 45 lbs)";
    case ResistanceBandLevel.HEAVY:
      return "Heavy (27 kg / 60 lbs)";
    case ResistanceBandLevel.EXTRA_HEAVY:
      return "Extra Heavy (41 kg / 90 lbs)";
    default:
      return `Level ${level}`;
  }
};

// Clean fields that don't belong to the training method
export const cleanSetsDataByTrainingMethod = (
  setsData: Record<string, any>,
  trainingMethod: string
): Record<string, WeightSetData | ResistanceBandSetData | TimedSetData> => {
  const cleanedSetsData: Record<string, any> = {};

  Object.keys(setsData).forEach((setKey) => {
    const setData = setsData[setKey];

    if (trainingMethod === "weight") {
      cleanedSetsData[setKey] = {
        reps: setData.reps ?? 1,
        weight: setData.weight ?? null,
        completed: setData.completed ?? false,
      } as WeightSetData;
    } else if (trainingMethod === "resistance_band") {
      cleanedSetsData[setKey] = {
        reps: setData.reps ?? 1,
        resistance_level: setData.resistance_level ?? null,
        completed: setData.completed ?? false,
      } as ResistanceBandSetData;
    } else if (trainingMethod === "timed") {
      cleanedSetsData[setKey] = {
        duration_seconds: setData.duration_seconds ?? 30,
        completed: setData.completed ?? false,
      } as TimedSetData;
    }
  });

  return cleanedSetsData;
};

// Get resistance band level options
export const getResistanceBandLevelOptions = (): Array<{
  value: number;
  label: string;
}> => {
  return [
    {
      value: ResistanceBandLevel.EXTRA_LIGHT,
      label: "Extra Light (5 kg / 10 lbs)",
    },
    { value: ResistanceBandLevel.LIGHT, label: "Light (11 kg / 25 lbs)" },
    { value: ResistanceBandLevel.MEDIUM, label: "Medium (20 kg / 45 lbs)" },
    { value: ResistanceBandLevel.HEAVY, label: "Heavy (27 kg / 60 lbs)" },
    {
      value: ResistanceBandLevel.EXTRA_HEAVY,
      label: "Extra Heavy (41 kg / 90 lbs)",
    },
  ];
};

// Get training method display name
export const getTrainingMethodLabel = (trainingMethod: string): string => {
  switch (trainingMethod) {
    case "weight":
      return "Weight";
    case "resistance_band":
      return "Resistance Band";
    case "timed":
      return "Timed";
    default:
      return trainingMethod;
  }
};

// Get training method badge color classes
export const getTrainingMethodColor = (trainingMethod: string): string => {
  switch (trainingMethod) {
    case "weight":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    case "resistance_band":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case "timed":
      return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
  }
};
